import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import RevokeInvite from "../RevokeInvite";
import ResendInvite from "../ResendInvite";
import { api } from "../../utils/axios-instance";
import { useDarkMode } from "../../context/darkModeContext";

export default function PendingInviteTable() {
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const { darkMode } = useDarkMode();

  useEffect(() => {
    const listPendingInvites = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/users/invites/pending`);

        setPendingInvites(
          (res.data || []).filter((i) =>
            ["pending", "sent", "expired"].includes(i.status)
          )
        );
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    listPendingInvites();
  }, []);

  // UPDATED COLUMNS
  const columns = [
    { id: "email", label: "Team Member", minWidth: 200 },
    { id: "statusText", label: "Status", minWidth: 150 },
    { id: "inviteSend", label: "Invite Send", minWidth: 120 },
    { id: "resendInvite", label: "Resend Invite", minWidth: 150 },
    { id: "revokeInvite", label: "Revoke Invite", minWidth: 150 },
  ];

  // Avatar fallback
  const getAvatar = (invite) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      invite.email
    )}&background=5E5ADB&color=fff`;
  };

  // STATUS DISPLAY FIX
  const getStatusText = () => {
    return "Not onboarded yet";
  };

  // INVITE STATUS (sent / expired)
  const getInviteSendText = (status) => {
    if (status === "expired") return "Expired";
    return "Sent";
  };

  // REMOVE ROW WITHOUT RELOAD
  const handleRevokeSuccess = (inviteId) => {
    setPendingInvites((prev) =>
      prev.filter((invite) => invite.id !== inviteId)
    );
  };

  return (
    <div>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    backgroundColor: darkMode ? "#0F172A" : "#FCFCFD",
                    fontWeight: 600,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {columns.map((col) => (
                    <TableCell key={col.id}>
                      <Skeleton variant="text" height={30} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : pendingInvites.length === 0 ? (
              <TableRow sx={{ backgroundColor: darkMode ? "#0F172A" : "#fff" }}>
                <TableCell
                  colSpan={columns.length}
                  sx={{ borderBottom: "none" }}
                >
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 0",
                      color: darkMode ? "#94A3B8" : "#667085",
                      fontSize: "14px",
                    }}
                  >
                    No pending invites. Invite new members to get started.
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              pendingInvites.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    backgroundColor: darkMode ? "#0F172A" : "#fff",
                    "&:hover": {
                      backgroundColor: darkMode ? "#1E293B" : "#F9FAFB",
                    },
                  }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        padding: "8px",
                        borderBottom: darkMode
                          ? "1px solid #334155"
                          : "1px solid #EAECF0",
                        color: darkMode ? "#E2E8F0" : "#101828",
                      }}
                    >
                      {/* TEAM MEMBER → EMAIL */}
                      {column.id === "email" ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Tooltip title={row.email} arrow>
                            <Avatar
                              src={getAvatar(row)}
                              alt={row.email}
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: "25%",
                              }}
                              variant="rounded"
                            />
                          </Tooltip>
                          <span style={{ fontSize: "14px", fontWeight: 500 }}>
                            {row.email}
                          </span>
                        </div>
                      ) : column.id === "statusText" ? (
                        getStatusText()
                      ) : column.id === "inviteSend" ? (
                        getInviteSendText(row.status)
                      ) : column.id === "resendInvite" ? (
                        <ResendInvite inviteId={row.id} email={row.email} />
                      ) : column.id === "revokeInvite" ? (
                        <RevokeInvite
                          inviteId={row.id}
                          onSuccess={handleRevokeSuccess}
                        />
                      ) : null}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
